import { useState, useEffect, useCallback, useRef } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, ChevronDown, ChevronRight, ThumbsUp, Send, Loader2, ArrowLeft, MessageSquare } from "lucide-react";
import { useAdminSupportNotifications } from "@/hooks/use-admin-support-notifications";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type TicketStatus = "open" | "in_progress" | "resolved";

interface Ticket {
  id: number;
  ticketRef: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: TicketStatus;
  createdAt: string;
}

interface TicketMessage {
  id: number;
  isAdmin: boolean;
  body: string;
  createdAt: string;
}

interface TicketDetail extends Ticket {
  messages: TicketMessage[];
}

type FeatureRequestStatus = "open" | "implemented";

interface FeatureRequest {
  id: number;
  title: string;
  description: string;
  upvotes: number;
  status: FeatureRequestStatus;
  createdAt: string;
  submitterName: string;
  submitterEmail: string | null;
}

const STATUS_LABELS: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
};

const STATUS_COLORS: Record<TicketStatus, string> = {
  open: "#3b82f6",
  in_progress: "#f59e0b",
  resolved: "#10b981",
};

function StatusBadge({ status }: { status: TicketStatus }) {
  const color = STATUS_COLORS[status] ?? "#6b7280";
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "0.125rem 0.625rem",
      borderRadius: "9999px",
      fontSize: "0.75rem",
      fontWeight: 500,
      background: `${color}20`,
      color,
      border: `1px solid ${color}40`,
    }}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function TicketThreadView({ ticketId, onBack, onStatusChange }: {
  ticketId: number;
  onBack: () => void;
  onStatusChange: (id: number, status: TicketStatus) => void;
}) {
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [updating, setUpdating] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const { markTicketRead, refresh: refreshNotifications } = useAdminSupportNotifications();

  useEffect(() => {
    setLoading(true);
    setError("");
    fetch(`${BASE}/api/admin/support/tickets/${ticketId}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to load ticket");
        const data: TicketDetail = await res.json();
        setTicket(data);
        await markTicketRead(ticketId);
        refreshNotifications();
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [ticketId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages]);

  const handleStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!ticket) return;
    const newStatus = e.target.value as TicketStatus;
    setUpdating(true);
    try {
      const res = await fetch(`${BASE}/api/admin/support/tickets/${ticket.id}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setTicket({ ...ticket, status: newStatus });
        onStatusChange(ticket.id, newStatus);
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleSend = async () => {
    if (!ticket || !replyText.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${BASE}/api/admin/support/tickets/${ticket.id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ body: replyText.trim() }),
      });
      if (!res.ok) throw new Error("Failed");
      const msg: TicketMessage = await res.json();
      setTicket({ ...ticket, messages: [...ticket.messages, msg] });
      setReplyText("");
    } catch {
      alert("Failed to send reply. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
        Loading ticket...
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div style={{ padding: "1rem", background: "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: "hsl(var(--destructive))" }}>
        {error || "Ticket not found"}
      </div>
    );
  }

  const allMessages: { isAdmin: boolean; body: string; createdAt: string; isOriginal?: boolean }[] = [
    { isAdmin: false, body: ticket.message, createdAt: ticket.createdAt, isOriginal: true },
    ...ticket.messages,
  ];

  return (
    <div>
      <button
        onClick={onBack}
        style={{ display: "flex", alignItems: "center", gap: "0.375rem", color: "hsl(var(--muted-foreground))", fontSize: "0.875rem", background: "none", border: "none", cursor: "pointer", marginBottom: "1.25rem", padding: 0 }}
      >
        <ArrowLeft style={{ width: "1rem", height: "1rem" }} />
        Back to tickets
      </button>

      <div style={{ marginBottom: "1.25rem", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: "1.125rem", fontWeight: 700, margin: "0 0 0.25rem" }}>{ticket.subject}</h2>
          <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", margin: 0 }}>
            {ticket.ticketRef} · {ticket.name} ({ticket.email}) · {new Date(ticket.createdAt).toLocaleDateString()}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <StatusBadge status={ticket.status} />
          <select
            value={ticket.status}
            onChange={handleStatusChange}
            disabled={updating}
            style={{
              fontSize: "0.8125rem",
              padding: "0.25rem 0.5rem",
              borderRadius: "0.375rem",
              border: "1px solid hsl(var(--border))",
              background: "hsl(var(--background))",
              color: "hsl(var(--foreground))",
              cursor: "pointer",
            }}
          >
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      <div style={{ border: "1px solid hsl(var(--border))", borderRadius: "0.5rem", overflow: "hidden", marginBottom: "1rem" }}>
        <div style={{ maxHeight: "30rem", overflowY: "auto", padding: "1rem", display: "flex", flexDirection: "column", gap: "0.875rem" }}>
          {allMessages.map((msg, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: msg.isAdmin ? "flex-end" : "flex-start",
              }}
            >
              <div style={{ maxWidth: "80%" }}>
                <p style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.25rem", fontWeight: 600, textAlign: msg.isAdmin ? "right" : "left" }}>
                  {msg.isAdmin ? "You (Admin)" : ticket.name}
                  {msg.isOriginal ? " (original message)" : ""}
                </p>
                <div
                  style={{
                    padding: "0.625rem 0.875rem",
                    borderRadius: msg.isAdmin ? "0.75rem 0.75rem 0.25rem 0.75rem" : "0.75rem 0.75rem 0.75rem 0.25rem",
                    background: msg.isAdmin ? "hsl(var(--primary))" : "hsl(var(--muted))",
                    color: msg.isAdmin ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
                    fontSize: "0.875rem",
                    lineHeight: "1.5",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {msg.body}
                </div>
                <p style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))", marginTop: "0.25rem", textAlign: msg.isAdmin ? "right" : "left" }}>
                  {new Date(msg.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      <div style={{ display: "flex", gap: "0.5rem" }}>
        <Textarea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="Type your reply to the user..."
          rows={3}
          style={{ flex: 1, resize: "vertical" }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button onClick={handleSend} disabled={sending || !replyText.trim()} style={{ alignSelf: "flex-end" }}>
          {sending ? <Loader2 style={{ width: "1rem", height: "1rem", animation: "spin 1s linear infinite" }} /> : <Send style={{ width: "1rem", height: "1rem" }} />}
        </Button>
      </div>
    </div>
  );
}

function TicketRow({ ticket, onStatusChange, onDelete, onOpen, unreadCount }: {
  ticket: Ticket;
  onStatusChange: (id: number, status: TicketStatus) => void;
  onDelete: (id: number) => void;
  onOpen: (id: number) => void;
  unreadCount: number;
}) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(`Delete ticket ${ticket.ticketRef}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`${BASE}/api/admin/support/tickets/${ticket.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        onDelete(ticket.id);
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error ?? "Failed to delete ticket");
      }
    } catch {
      alert("Failed to delete ticket");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <tr
      style={{ borderBottom: "1px solid hsl(var(--border))", cursor: "pointer" }}
      onClick={() => onOpen(ticket.id)}
    >
      <td style={{ padding: "0.75rem 1rem", whiteSpace: "nowrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ fontFamily: "monospace", fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>{ticket.ticketRef}</span>
          {unreadCount > 0 && (
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "1.125rem",
              height: "1.125rem",
              padding: "0 0.2rem",
              borderRadius: "9999px",
              background: "#ef4444",
              color: "#fff",
              fontSize: "0.625rem",
              fontWeight: 700,
              flexShrink: 0,
            }}>
              {unreadCount}
            </span>
          )}
        </div>
      </td>
      <td style={{ padding: "0.75rem 1rem" }}>
        <div style={{ fontWeight: 500, fontSize: "0.875rem" }}>{ticket.name}</div>
        <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>{ticket.email}</div>
      </td>
      <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem" }}>{ticket.subject}</td>
      <td style={{ padding: "0.75rem 1rem" }}>
        <StatusBadge status={ticket.status} />
      </td>
      <td style={{ padding: "0.75rem 1rem", fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap" }}>
        {new Date(ticket.createdAt).toLocaleDateString()}
      </td>
      <td style={{ padding: "0.75rem 1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }} onClick={(e) => e.stopPropagation()}>
          <ChevronRight style={{ width: "1rem", height: "1rem", color: "hsl(var(--muted-foreground))" }} />
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0.25rem",
              color: "hsl(var(--destructive))",
              display: "flex",
              alignItems: "center",
              opacity: deleting ? 0.5 : 1,
            }}
            title="Delete ticket"
          >
            <Trash2 style={{ width: "1rem", height: "1rem" }} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function readTicketIdFromUrl(): number | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("ticketId");
  if (!raw) return null;
  const id = parseInt(raw, 10);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function TicketsTab() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(() => readTicketIdFromUrl());
  const { unreadByTicket } = useAdminSupportNotifications();

  useEffect(() => {
    if (selectedTicketId !== null && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      if (url.searchParams.get("ticketId")) {
        url.searchParams.delete("ticketId");
        window.history.replaceState({}, "", url.toString());
      }
    }
  }, [selectedTicketId]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = statusFilter && statusFilter !== "all"
        ? `${BASE}/api/admin/support/tickets?status=${statusFilter}`
        : `${BASE}/api/admin/support/tickets`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch tickets");
      setTickets(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleStatusChange = (id: number, status: TicketStatus) => {
    setTickets((prev) => prev.map((t) => t.id === id ? { ...t, status } : t));
  };

  const handleDelete = (id: number) => {
    setTickets((prev) => prev.filter((t) => t.id !== id));
    if (selectedTicketId === id) setSelectedTicketId(null);
  };

  if (selectedTicketId !== null) {
    return (
      <TicketThreadView
        ticketId={selectedTicketId}
        onBack={() => setSelectedTicketId(null)}
        onStatusChange={handleStatusChange}
      />
    );
  }

  if (loading) {
    return <div style={{ padding: "2rem", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>Loading tickets...</div>;
  }

  if (error) {
    return <div style={{ padding: "1rem", background: "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: "hsl(var(--destructive))" }}>{error}</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.25rem" }}>
        <label style={{ fontSize: "0.875rem", fontWeight: 500 }}>Filter by status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            fontSize: "0.875rem",
            padding: "0.375rem 0.75rem",
            borderRadius: "0.375rem",
            border: "1px solid hsl(var(--border))",
            background: "hsl(var(--background))",
            color: "hsl(var(--foreground))",
            cursor: "pointer",
          }}
        >
          <option value="all">All</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
        </select>
        <span style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>
          {tickets.length} ticket{tickets.length !== 1 ? "s" : ""}
        </span>
      </div>

      {tickets.length === 0 ? (
        <div style={{ padding: "3rem", textAlign: "center", color: "hsl(var(--muted-foreground))", background: "hsl(var(--muted) / 0.3)", borderRadius: "0.5rem" }}>
          No tickets found
        </div>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: "0.5rem", border: "1px solid hsl(var(--border))" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
            <thead>
              <tr style={{ background: "hsl(var(--muted) / 0.5)", borderBottom: "1px solid hsl(var(--border))" }}>
                <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>Ref</th>
                <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>User</th>
                <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>Subject</th>
                <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>Status</th>
                <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>Date</th>
                <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <TicketRow
                  key={ticket.id}
                  ticket={ticket}
                  onStatusChange={handleStatusChange}
                  onDelete={handleDelete}
                  onOpen={setSelectedTicketId}
                  unreadCount={unreadByTicket[ticket.id] ?? 0}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FeatureRequestsTab() {
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() => {
    fetch(`${BASE}/api/admin/support/feature-requests`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to fetch feature requests");
        return res.json();
      })
      .then((data) => {
        setRequests(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this feature request? This cannot be undone.")) return;
    try {
      const res = await fetch(`${BASE}/api/admin/support/feature-requests/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setRequests((prev) => prev.filter((r) => r.id !== id));
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error ?? "Failed to delete feature request");
      }
    } catch {
      alert("Failed to delete feature request");
    }
  };

  const handleToggleStatus = async (req: FeatureRequest) => {
    if (togglingId === req.id) return;
    const newStatus: FeatureRequestStatus = req.status === "implemented" ? "open" : "implemented";
    setTogglingId(req.id);
    try {
      const res = await fetch(`${BASE}/api/admin/support/feature-requests/${req.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, status: newStatus } : r));
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error ?? "Failed to update status");
      }
    } catch {
      alert("Failed to update status");
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return <div style={{ padding: "2rem", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>Loading feature requests...</div>;
  }

  if (error) {
    return <div style={{ padding: "1rem", background: "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: "hsl(var(--destructive))" }}>{error}</div>;
  }

  if (requests.length === 0) {
    return (
      <div style={{ padding: "3rem", textAlign: "center", color: "hsl(var(--muted-foreground))", background: "hsl(var(--muted) / 0.3)", borderRadius: "0.5rem" }}>
        No feature requests yet
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
      <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.25rem" }}>
        {requests.length} request{requests.length !== 1 ? "s" : ""}, sorted by votes
      </div>
      {requests.map((req) => (
        <Card key={req.id} style={{ opacity: req.status === "implemented" ? 0.85 : 1 }}>
          <CardContent style={{ padding: "1rem" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.375rem",
                    padding: "0.25rem 0.625rem",
                    background: "hsl(var(--primary) / 0.1)",
                    borderRadius: "9999px",
                    color: "hsl(var(--primary))",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                  }}>
                    <ThumbsUp style={{ width: "0.875rem", height: "0.875rem" }} />
                    {req.upvotes}
                  </div>
                  <h3 style={{ fontWeight: 600, fontSize: "0.9375rem", margin: 0 }}>{req.title}</h3>
                  {req.status === "implemented" && (
                    <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "0.125rem 0.625rem",
                      borderRadius: "9999px",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      background: "#10b98120",
                      color: "#10b981",
                      border: "1px solid #10b98140",
                    }}>
                      Implemented
                    </span>
                  )}
                </div>
                <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", margin: "0 0 0.5rem", lineHeight: 1.5 }}>
                  {req.description}
                </p>
                <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>
                  Submitted by {req.submitterName}
                  {req.submitterEmail && ` (${req.submitterEmail})`}
                  {" · "}{new Date(req.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                <button
                  onClick={() => handleToggleStatus(req)}
                  disabled={togglingId === req.id}
                  style={{
                    background: req.status === "implemented" ? "hsl(var(--muted) / 0.5)" : "#10b98115",
                    border: req.status === "implemented" ? "1px solid hsl(var(--border))" : "1px solid #10b98140",
                    cursor: togglingId === req.id ? "wait" : "pointer",
                    padding: "0.25rem 0.625rem",
                    borderRadius: "0.375rem",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: req.status === "implemented" ? "hsl(var(--muted-foreground))" : "#10b981",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    whiteSpace: "nowrap",
                  }}
                  title={req.status === "implemented" ? "Mark as Open" : "Mark as Implemented"}
                >
                  {togglingId === req.id ? (
                    <Loader2 style={{ width: "0.75rem", height: "0.75rem" }} className="animate-spin" />
                  ) : null}
                  {req.status === "implemented" ? "Mark as Open" : "Mark as Implemented"}
                </button>
                <button
                  onClick={() => handleDelete(req.id)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0.25rem",
                    color: "hsl(var(--destructive))",
                    display: "flex",
                    alignItems: "center",
                  }}
                  title="Delete feature request"
                >
                  <Trash2 style={{ width: "1rem", height: "1rem" }} />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function AdminSupport() {
  usePageTitle("Admin – Support");
  const [activeTab, setActiveTab] = useState<"tickets" | "feature-requests">("tickets");
  const { totalUnread } = useAdminSupportNotifications();

  const tabStyle = (tab: string) => ({
    padding: "0.625rem 1.25rem",
    borderRadius: "0.375rem",
    fontSize: "0.9375rem",
    fontWeight: activeTab === tab ? 600 : 400,
    cursor: "pointer",
    background: activeTab === tab ? "hsl(var(--primary))" : "transparent",
    color: activeTab === tab ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
    border: "none",
    transition: "background 0.15s, color 0.15s",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  });

  return (
    <div>
      <div style={{ marginBottom: "2rem" }}>
        <h1 style={{ fontSize: "1.75rem", fontWeight: 700, marginBottom: "0.25rem" }}>Support</h1>
        <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.9375rem" }}>
          Manage support tickets and feature requests from users
        </p>
      </div>

      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem", padding: "0.25rem", background: "hsl(var(--muted) / 0.5)", borderRadius: "0.5rem", width: "fit-content" }}>
        <button style={tabStyle("tickets")} onClick={() => setActiveTab("tickets")}>
          Support Tickets
          {totalUnread > 0 && (
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "1.125rem",
              height: "1.125rem",
              padding: "0 0.2rem",
              borderRadius: "9999px",
              background: activeTab === "tickets" ? "rgba(255,255,255,0.3)" : "#ef4444",
              color: "#fff",
              fontSize: "0.625rem",
              fontWeight: 700,
            }}>
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
        <button style={tabStyle("feature-requests")} onClick={() => setActiveTab("feature-requests")}>
          Feature Requests
        </button>
      </div>

      {activeTab === "tickets" ? <TicketsTab /> : <FeatureRequestsTab />}
    </div>
  );
}
